<template>
  <v-dialog
    v-model="show"
    :persistent="isForced"
    max-width="340"
  >
    <v-card>
      <v-card-title class="text-h5" style="word-break: keep-all;">
        {{ dialogTitle }}
      </v-card-title>
      <v-card-text v-if="updateStarted">
        Programmet uppdateras, vänligen vänta
        <v-progress-linear
          indeterminate
          color="green"
          class="mb-0"
        ></v-progress-linear>
      </v-card-text>
      <v-card-text v-else>
        {{ updateText }}
      </v-card-text>
      <v-card-actions>
        <v-btn
          v-if="!isForced"
          @click="dismiss"
          color="grey"
          text
          :disabled="updateStarted"
        >
          Senare
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          @click="update"
          color="success"
          text
          :disabled="updateStarted"
        >
          {{ buttonText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'UpdateDialog',
  data: () => ({
    updateStarted: false
  }),
  computed: {
    ...mapState(
      {
        showDialog: state => state.showUpdateDialog,
        isElectron: state => state.isElectron,
        updateType: state => state.updateType,
        updateSource: state => state.updateSource,
        hasHardwareAccess: state => state.hasHardwareAccess
      }
    ),
    show: {
      get () {
        return this.showDialog
      },
      set (val) {
        this.$store.commit('SET_SHOW_UPDATE_DIALOG', val)
      }
    },
    isForced: function () {
      return this.updateSource === 'forced'
    },
    isElectronUpdate: function () {
      // Lita på serversignalen för uppdateringstyp istället för att detektera miljö
      return this.updateType === 'electron'
    },
    dialogTitle: function () {
      return this.isForced ? 'Uppdatering krävs' : 'Uppdatering tillgänglig'
    },
    buttonText: function () {
      return this.isElectronUpdate ? 'Uppdatera' : 'Ladda om'
    },
    updateText: function () {
      if (this.isForced) {
        return this.isElectronUpdate
          ? 'Uppdatering krävs för att fortsätta, programmet kommer att startas om'
          : 'Uppdatering krävs för att fortsätta, sidan behöver laddas om'
      }
      return 'En ny version är tillgänglig. Uppdatera när det passar dig.'
    }
  },
  methods: {
    dismiss: function () {
      this.$store.commit('SET_SHOW_UPDATE_DIALOG', false)
    },
    // Egen anteckning: Kontrollerar uppdatering leder till installation, efter installation hamnar den i waiting till slut activated
    update: async function () {
      // Rensa uppdateringsindikator när uppdatering påbörjas
      this.$store.commit('SET_HAS_WAITING_UPDATE', false)

      if (this.isElectronUpdate) {
        // Full electron-uppdatering - använd electrons egen uppdateringsmetod
        console.log('Electron uppdatering påbörjad')
        this.updateStarted = true
        this.$store.dispatch('updateClient')
      } else if (this.isElectron && this.hasHardwareAccess) {
        // PWA-uppdatering inuti Electron - använd softUpdate
        console.log('PWA uppdatering inuti Electron påbörjad')
        this.updateStarted = true
        window.hardware.softUpdate()
      } else if ('serviceWorker' in navigator) {
        // Browser with service worker support - use PWA update
        console.log('PWA uppdatering påbörjad')
        this.updateStarted = true
        await this.handlePWAUpdate()
      } else {
        // Fallback for browsers/devices without PWA support
        console.log('Inget PWA stöd, laddar om sidan')
        window.location.reload()
      }
    },
    handlePWAUpdate: async function () {
      setTimeout(() => {
        // Denna uppdatering ska bara ladda ner src mappen så borde inte ta så lång tid
        // Uppdatering av serviceworkers kan fastna eller hamna i ett oväntat state med flera flikar
        // Har troligen slutförts redan annars får man försöka igen, bättre än att det fortsätter ladda
        console.log('Uppdatering troligen fastnat, laddar om sidan')
        window.location.reload()
      }, 30000)
      try {
        const registration = await navigator.serviceWorker.getRegistration()
        if (registration) {
          this.setupUpdateListeners(registration)
          registration.update().then(() => {
            if (registration.installing) {
              registration.installing.addEventListener('statechange', (e) => {
                if (e.target.state === 'installed') {
                  this.checkForWaitingUpdate(registration)
                }
              })
            } else if (registration.waiting) {
              this.checkForWaitingUpdate(registration)
            } else {
              console.log('Ingen uppdatering tillgänglig, laddar om sidan')
              window.location.reload()
            }
          }).catch(error => {
            console.log('Uppdatering misslyckades, laddar om sidan')
            console.error('Service worker update error:', error)
            window.location.reload()
          })
        } else {
          // Webbläsaren har stöd men har inte installerat (exempelvis första besök eller incognito)
          console.log('Ingen serviceworker eller sparade assets alternativt redan uppdaterad, laddar om sidan')
          window.location.reload()
        }
      } catch (error) {
        // Webbläsaren har stöd men är inte kompatibel med serviceworkern så den fungerar som vanliga spa
        console.log('Installation av serviceworker misslyckades, laddar om sidan')
        console.error('Service worker installation error:', error)
        window.location.reload()
      }
    },
    setupUpdateListeners: function (registration) {
      // Bevakar uppdateringar som triggas nedan och laddar om när det är klart
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing
        newWorker.addEventListener('statechange', (e) => {
          if (e.target.state === 'activated') {
            console.log('Uppdatering klar, laddar om sidan')
            window.location.reload()
          }
        })
      })

      // För att upptäcka uppdateringar där inget updatefound event skapas
      // Räcker inte med enbart detta så ovan behövs också
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('Uppdatering klar, laddar om sidan')
        window.location.reload()
      })
    },
    checkForWaitingUpdate: function (registration) {
      // registration är installerad version, registration.waiting är den nya versionen
      if (registration.waiting) {
        console.log('Hittat uppdatering som väntar, tvingar uppdatering')
        // PWA kan normalt inte bli uppdaterade förrän alla flikar stängts, en vanlig reload räcker inte
        // Denna metod tvingar fram uppdateringen ändå så att en reload ger den nya versionen efteråt
        registration.waiting.postMessage({ type: 'SKIP_WAITING' })
      } else {
        console.log('Ingen väntande uppdatering hittades')
      }
    }
  }
}
</script>
